/**
 * rooms.ts - Room type definitions (75 rooms, each exclusive to 1 theme)
 */

import type { PropId } from './props.js';

export type FearType = 'visual' | 'psychological' | 'physical' | 'auditory';

export type RoomTypeId =
  // Haunted House (7)
  | 'dark_corridor'
  | 'mirror_room'
  | 'graveyard_room'
  | 'coffin_room'
  | 'kitchen'
  | 'bathroom'
  | 'secret_passage'
  // Abandoned Hospital (6)
  | 'lab'
  | 'morgue'
  | 'surgery_room'
  | 'ward_room'
  | 'reception_hall'
  | 'x_ray_room'
  // School Back Mountain (8)
  | 'doll_room'
  | 'music_room'
  | 'attic'
  | 'science_lab'
  | 'classroom'
  | 'art_room'
  | 'gymnasium'
  | 'rooftop'
  // Cursed Circus (7)
  | 'funhouse'
  | 'trapeze_room'
  | 'freak_show'
  | 'ticket_booth'
  | 'animal_cage'
  | 'clown_tent'
  | 'hall_of_mirrors'
  // Ancient Temple (6)
  | 'sacrifice_altar'
  | 'treasure_vault'
  | 'underground_river'
  | 'curse_chamber'
  | 'mummy_tomb'
  | 'crypt'
  // Pirate Ship (7)
  | 'captain_cabin'
  | 'cargo_hold'
  | 'davy_jones_locker'
  | 'brig'
  | 'crows_nest'
  | 'powder_room'
  | 'navigation_room'
  // Abandoned Mine (9)
  | 'mine_shaft'
  | 'crystal_cave'
  | 'explosives_storage'
  | 'underground_lake'
  | 'rail_tunnel'
  | 'elevator_shaft'
  | 'ore_processing'
  | 'collapsed_tunnel'
  | 'miners_quarters'
  // Gothic Castle (8)
  | 'throne_room'
  | 'wine_cellar'
  | 'tower_chamber'
  | 'dungeon'
  | 'library'
  | 'banquet_hall'
  | 'chapel'
  | 'torture_chamber'
  // Underground Bunker (8)
  | 'server_room'
  | 'quarantine_zone'
  | 'armory'
  | 'command_center'
  | 'ventilation_shaft'
  | 'decontamination'
  | 'armory_hall'
  | 'science_room'
  // Cursed Forest (9)
  | 'witch_hut'
  | 'treehouse'
  | 'swamp_path'
  | 'fairy_ring'
  | 'ancient_ruin'
  | 'hollow_tree'
  | 'spider_nest'
  | 'moonlit_clearing'
  | 'abandoned_cabin';

export interface RoomTypeAffinity {
  fearType: FearType;
  bonus: number; // multiplier applied when ghost's fear type matches
}

export interface RoomBaseStats {
  fear: number;
  thrill: number;
  atmosphere: number;
  fun: number;
}

export interface RoomUnlockCondition {
  minDay?: number;
  minFame?: number;
  requiredRoom?: RoomTypeId;
}

export interface RoomTypeDef {
  id: RoomTypeId;
  nameKey: string;
  descKey: string;
  baseAffinities: RoomTypeAffinity[];
  baseStats: RoomBaseStats;  // 4 emotion stats (0-100)
  unlockCost: number; // SP to unlock this room type
  unlockCondition?: RoomUnlockCondition;
  imagePath: string;  // path to preview image (webp)
  availableProps: PropId[];  // installable props for this room type
  themeTags: string[];
}

export const ROOM_TYPES: Record<RoomTypeId, RoomTypeDef> = {
  // ═══════════════════════════════════════════════
  // ORIGINAL ROOMS (10)
  // ═══════════════════════════════════════════════
  dark_corridor: {
    id: 'dark_corridor',
    nameKey: 'room_dark_corridor',
    descKey: 'room_dark_corridor_desc',
    baseAffinities: [{ fearType: 'psychological', bonus: 0.3 }],
    baseStats: { fear: 30, thrill: 10, atmosphere: 50, fun: 5 },
    unlockCost: 0,
    imagePath: '/images/room/dark-corridor.webp',
    availableProps: ['cobwebs', 'candles', 'chains', 'dry_ice', 'scent_sprayer', 'auto_lock'],
    themeTags: ['haunted_house'],
  },
  mirror_room: {
    id: 'mirror_room',
    nameKey: 'room_mirror_room',
    descKey: 'room_mirror_room_desc',
    baseAffinities: [
      { fearType: 'visual', bonus: 0.25 },
      { fearType: 'psychological', bonus: 0.15 },
    ],
    baseStats: { fear: 40, thrill: 20, atmosphere: 40, fun: 10 },
    unlockCost: 0,
    imagePath: '/images/room/mirror-room.webp',
    availableProps: ['candles', 'strobe_light', 'hologram_device', 'scent_sprayer', 'skull_decor'],
    themeTags: ['haunted_house'],
  },
  dungeon: {
    id: 'dungeon',
    nameKey: 'room_dungeon',
    descKey: 'room_dungeon_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.2 },
      { fearType: 'physical', bonus: 0.2 },
    ],
    baseStats: { fear: 50, thrill: 30, atmosphere: 30, fun: 5 },
    unlockCost: 0,
    imagePath: '/images/room/dungeon.webp',
    availableProps: ['chains', 'skull_decor', 'blood_pool', 'dry_ice', 'vibration_floor', 'temp_controller'],
    themeTags: ['gothic_castle'],
  },
  graveyard_room: {
    id: 'graveyard_room',
    nameKey: 'room_graveyard',
    descKey: 'room_graveyard_desc',
    baseAffinities: [{ fearType: 'psychological', bonus: 0.4 }],
    baseStats: { fear: 35, thrill: 15, atmosphere: 55, fun: 5 },
    unlockCost: 1000,
    imagePath: '/images/room/graveyard.webp',
    availableProps: ['cobwebs', 'dry_ice', 'scent_sprayer', 'skull_decor', 'hologram_device'],
    themeTags: ['haunted_house'],
  },
  lab: {
    id: 'lab',
    nameKey: 'room_lab',
    descKey: 'room_lab_desc',
    baseAffinities: [
      { fearType: 'visual', bonus: 0.3 },
      { fearType: 'physical', bonus: 0.2 },
    ],
    baseStats: { fear: 25, thrill: 40, atmosphere: 20, fun: 20 },
    unlockCost: 0,
    imagePath: '/images/room/lab.webp',
    availableProps: ['strobe_light', 'surround_speaker', 'temp_controller', 'vibration_floor', 'hologram_device'],
    themeTags: ['abandoned_hospital'],
  },
  library: {
    id: 'library',
    nameKey: 'room_library',
    descKey: 'room_library_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.35 },
      { fearType: 'auditory', bonus: 0.15 },
    ],
    baseStats: { fear: 20, thrill: 10, atmosphere: 60, fun: 15 },
    unlockCost: 0,
    imagePath: '/images/room/library.webp',
    availableProps: ['cobwebs', 'candles', 'eerie_painting', 'scent_sprayer', 'surround_speaker'],
    themeTags: ['gothic_castle'],
  },
  coffin_room: {
    id: 'coffin_room',
    nameKey: 'room_coffin_room',
    descKey: 'room_coffin_room_desc',
    baseAffinities: [
      { fearType: 'visual', bonus: 0.3 },
      { fearType: 'psychological', bonus: 0.2 },
    ],
    baseStats: { fear: 60, thrill: 25, atmosphere: 35, fun: 5 },
    unlockCost: 5000,
    imagePath: '/images/room/coffin-room.webp',
    availableProps: ['dry_ice', 'auto_lock', 'vibration_floor', 'temp_controller', 'chains'],
    themeTags: ['haunted_house'],
  },
  doll_room: {
    id: 'doll_room',
    nameKey: 'room_doll_room',
    descKey: 'room_doll_room_desc',
    baseAffinities: [
      { fearType: 'visual', bonus: 0.2 },
      { fearType: 'psychological', bonus: 0.35 },
    ],
    baseStats: { fear: 55, thrill: 15, atmosphere: 45, fun: 10 },
    unlockCost: 8000,
    unlockCondition: { minDay: 5 },
    imagePath: '/images/room/doll-room.webp',
    availableProps: ['strobe_light', 'surround_speaker', 'hologram_device', 'scent_sprayer', 'auto_lock'],
    themeTags: ['haunted_house'],
  },
  kitchen: {
    id: 'kitchen',
    nameKey: 'room_kitchen',
    descKey: 'room_kitchen_desc',
    baseAffinities: [
      { fearType: 'physical', bonus: 0.3 },
      { fearType: 'auditory', bonus: 0.2 },
    ],
    baseStats: { fear: 20, thrill: 15, atmosphere: 25, fun: 40 },
    unlockCost: 12000,
    unlockCondition: { minDay: 3 },
    imagePath: '/images/room/kitchen.webp',
    availableProps: ['blood_pool', 'candles', 'cobwebs', 'temp_controller', 'surround_speaker'],
    themeTags: ['haunted_house'],
  },
  bathroom: {
    id: 'bathroom',
    nameKey: 'room_bathroom',
    descKey: 'room_bathroom_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.3 },
      { fearType: 'auditory', bonus: 0.25 },
    ],
    baseStats: { fear: 30, thrill: 35, atmosphere: 20, fun: 20 },
    unlockCost: 20000,
    unlockCondition: { minDay: 7, minFame: 50 },
    imagePath: '/images/room/bathroom.webp',
    availableProps: ['blood_pool', 'strobe_light', 'auto_lock', 'vibration_floor', 'scent_sprayer'],
    themeTags: ['haunted_house'],
  },

  // ═══════════════════════════════════════════════
  // CURSED CIRCUS (6)
  // ═══════════════════════════════════════════════
  funhouse: {
    id: 'funhouse',
    nameKey: 'room_funhouse',
    descKey: 'room_funhouse_desc',
    baseAffinities: [
      { fearType: 'visual', bonus: 0.35 },
      { fearType: 'psychological', bonus: 0.2 },
    ],
    baseStats: { fear: 25, thrill: 35, atmosphere: 20, fun: 35 },
    unlockCost: 0,
    imagePath: '/images/room/funhouse.webp',
    availableProps: ['strobe_light', 'hologram_device', 'vibration_floor', 'scent_sprayer'],
    themeTags: ['cursed_circus'],
  },
  trapeze_room: {
    id: 'trapeze_room',
    nameKey: 'room_trapeze_room',
    descKey: 'room_trapeze_room_desc',
    baseAffinities: [
      { fearType: 'physical', bonus: 0.3 },
      { fearType: 'visual', bonus: 0.15 },
    ],
    baseStats: { fear: 15, thrill: 50, atmosphere: 15, fun: 30 },
    unlockCost: 6000,
    unlockCondition: { requiredRoom: 'funhouse' },
    imagePath: '/images/room/trapeze-room.webp',
    availableProps: ['strobe_light', 'auto_lock', 'vibration_floor', 'surround_speaker'],
    themeTags: ['cursed_circus'],
  },
  freak_show: {
    id: 'freak_show',
    nameKey: 'room_freak_show',
    descKey: 'room_freak_show_desc',
    baseAffinities: [
      { fearType: 'visual', bonus: 0.4 },
      { fearType: 'psychological', bonus: 0.3 },
    ],
    baseStats: { fear: 60, thrill: 20, atmosphere: 30, fun: 5 },
    unlockCost: 25000,
    unlockCondition: { minDay: 10, minFame: 100, requiredRoom: 'funhouse' },
    imagePath: '/images/room/freak-show.webp',
    availableProps: ['hologram_device', 'strobe_light', 'blood_pool', 'surround_speaker', 'auto_lock'],
    themeTags: ['cursed_circus'],
  },
  ticket_booth: {
    id: 'ticket_booth',
    nameKey: 'room_ticket_booth',
    descKey: 'room_ticket_booth_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.25 },
      { fearType: 'visual', bonus: 0.2 },
    ],
    baseStats: { fear: 20, thrill: 15, atmosphere: 35, fun: 30 },
    unlockCost: 0,
    imagePath: '/images/room/ticket-booth.webp',
    availableProps: ['cobwebs', 'strobe_light', 'scent_sprayer', 'eerie_painting'],
    themeTags: ['cursed_circus'],
  },
  animal_cage: {
    id: 'animal_cage',
    nameKey: 'room_animal_cage',
    descKey: 'room_animal_cage_desc',
    baseAffinities: [
      { fearType: 'physical', bonus: 0.3 },
      { fearType: 'visual', bonus: 0.25 },
    ],
    baseStats: { fear: 45, thrill: 30, atmosphere: 25, fun: 10 },
    unlockCost: 8000,
    unlockCondition: { minDay: 5, requiredRoom: 'funhouse' },
    imagePath: '/images/room/animal-cage.webp',
    availableProps: ['chains', 'blood_pool', 'surround_speaker', 'scent_sprayer', 'auto_lock'],
    themeTags: ['cursed_circus'],
  },
  clown_tent: {
    id: 'clown_tent',
    nameKey: 'room_clown_tent',
    descKey: 'room_clown_tent_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.4 },
      { fearType: 'visual', bonus: 0.2 },
    ],
    baseStats: { fear: 55, thrill: 15, atmosphere: 35, fun: 10 },
    unlockCost: 15000,
    unlockCondition: { minDay: 8, requiredRoom: 'funhouse' },
    imagePath: '/images/room/clown-tent.webp',
    availableProps: ['hologram_device', 'strobe_light', 'surround_speaker', 'dry_ice', 'auto_lock'],
    themeTags: ['cursed_circus'],
  },
  hall_of_mirrors: {
    id: 'hall_of_mirrors',
    nameKey: 'room_hall_of_mirrors',
    descKey: 'room_hall_of_mirrors_desc',
    baseAffinities: [
      { fearType: 'visual', bonus: 0.4 },
      { fearType: 'psychological', bonus: 0.3 },
    ],
    baseStats: { fear: 50, thrill: 25, atmosphere: 30, fun: 15 },
    unlockCost: 20000,
    unlockCondition: { minDay: 12, minFame: 100, requiredRoom: 'trapeze_room' },
    imagePath: '/images/room/hall-of-mirrors.webp',
    availableProps: ['strobe_light', 'hologram_device', 'vibration_floor', 'surround_speaker', 'scent_sprayer'],
    themeTags: ['cursed_circus'],
  },

  // ═══════════════════════════════════════════════
  // ANCIENT TEMPLE (6)
  // ═══════════════════════════════════════════════
  sacrifice_altar: {
    id: 'sacrifice_altar',
    nameKey: 'room_sacrifice_altar',
    descKey: 'room_sacrifice_altar_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.4 },
      { fearType: 'visual', bonus: 0.2 },
    ],
    baseStats: { fear: 55, thrill: 15, atmosphere: 45, fun: 0 },
    unlockCost: 0,
    imagePath: '/images/room/sacrifice-altar.webp',
    availableProps: ['candles', 'blood_pool', 'skull_decor', 'scent_sprayer', 'dry_ice'],
    themeTags: ['ancient_temple'],
  },
  treasure_vault: {
    id: 'treasure_vault',
    nameKey: 'room_treasure_vault',
    descKey: 'room_treasure_vault_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.2 },
      { fearType: 'auditory', bonus: 0.2 },
    ],
    baseStats: { fear: 20, thrill: 10, atmosphere: 50, fun: 25 },
    unlockCost: 0,
    imagePath: '/images/room/treasure-vault.webp',
    availableProps: ['cobwebs', 'eerie_painting', 'surround_speaker', 'auto_lock'],
    themeTags: ['ancient_temple'],
  },
  underground_river: {
    id: 'underground_river',
    nameKey: 'room_underground_river',
    descKey: 'room_underground_river_desc',
    baseAffinities: [
      { fearType: 'auditory', bonus: 0.3 },
      { fearType: 'psychological', bonus: 0.2 },
    ],
    baseStats: { fear: 25, thrill: 15, atmosphere: 55, fun: 15 },
    unlockCost: 15000,
    unlockCondition: { minDay: 8, requiredRoom: 'sacrifice_altar' },
    imagePath: '/images/room/underground-river.webp',
    availableProps: ['scent_sprayer', 'surround_speaker', 'temp_controller', 'dry_ice'],
    themeTags: ['ancient_temple'],
  },
  curse_chamber: {
    id: 'curse_chamber',
    nameKey: 'room_curse_chamber',
    descKey: 'room_curse_chamber_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.45 },
      { fearType: 'physical', bonus: 0.15 },
    ],
    baseStats: { fear: 65, thrill: 20, atmosphere: 40, fun: 0 },
    unlockCost: 30000,
    unlockCondition: { minDay: 15, minFame: 200, requiredRoom: 'sacrifice_altar' },
    imagePath: '/images/room/curse-chamber.webp',
    availableProps: ['chains', 'blood_pool', 'hologram_device', 'surround_speaker', 'auto_lock'],
    themeTags: ['ancient_temple'],
  },
  mummy_tomb: {
    id: 'mummy_tomb',
    nameKey: 'room_mummy_tomb',
    descKey: 'room_mummy_tomb_desc',
    baseAffinities: [
      { fearType: 'visual', bonus: 0.3 },
      { fearType: 'psychological', bonus: 0.3 },
    ],
    baseStats: { fear: 50, thrill: 10, atmosphere: 50, fun: 5 },
    unlockCost: 18000,
    unlockCondition: { minFame: 100 },
    imagePath: '/images/room/mummy-tomb.webp',
    availableProps: ['cobwebs', 'candles', 'dry_ice', 'skull_decor', 'eerie_painting'],
    themeTags: ['ancient_temple'],
  },

  // ═══════════════════════════════════════════════
  // PIRATE SHIP (6)
  // ═══════════════════════════════════════════════
  captain_cabin: {
    id: 'captain_cabin',
    nameKey: 'room_captain_cabin',
    descKey: 'room_captain_cabin_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.3 },
      { fearType: 'visual', bonus: 0.2 },
    ],
    baseStats: { fear: 35, thrill: 15, atmosphere: 45, fun: 15 },
    unlockCost: 0,
    imagePath: '/images/room/captain-cabin.webp',
    availableProps: ['candles', 'eerie_painting', 'cobwebs', 'surround_speaker', 'scent_sprayer'],
    themeTags: ['pirate_ship'],
  },
  cargo_hold: {
    id: 'cargo_hold',
    nameKey: 'room_cargo_hold',
    descKey: 'room_cargo_hold_desc',
    baseAffinities: [
      { fearType: 'physical', bonus: 0.25 },
      { fearType: 'auditory', bonus: 0.2 },
    ],
    baseStats: { fear: 30, thrill: 25, atmosphere: 30, fun: 10 },
    unlockCost: 0,
    imagePath: '/images/room/cargo-hold.webp',
    availableProps: ['chains', 'cobwebs', 'vibration_floor', 'temp_controller', 'dry_ice'],
    themeTags: ['pirate_ship'],
  },
  davy_jones_locker: {
    id: 'davy_jones_locker',
    nameKey: 'room_davy_jones_locker',
    descKey: 'room_davy_jones_locker_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.35 },
      { fearType: 'visual', bonus: 0.25 },
    ],
    baseStats: { fear: 60, thrill: 20, atmosphere: 35, fun: 5 },
    unlockCost: 22000,
    unlockCondition: { minDay: 12, minFame: 150, requiredRoom: 'cargo_hold' },
    imagePath: '/images/room/davy-jones-locker.webp',
    availableProps: ['blood_pool', 'hologram_device', 'surround_speaker', 'dry_ice', 'auto_lock'],
    themeTags: ['pirate_ship'],
  },
  brig: {
    id: 'brig',
    nameKey: 'room_brig',
    descKey: 'room_brig_desc',
    baseAffinities: [
      { fearType: 'physical', bonus: 0.3 },
      { fearType: 'psychological', bonus: 0.2 },
    ],
    baseStats: { fear: 45, thrill: 30, atmosphere: 20, fun: 5 },
    unlockCost: 10000,
    unlockCondition: { requiredRoom: 'captain_cabin' },
    imagePath: '/images/room/brig.webp',
    availableProps: ['chains', 'auto_lock', 'vibration_floor', 'blood_pool'],
    themeTags: ['pirate_ship'],
  },
  crows_nest: {
    id: 'crows_nest',
    nameKey: 'room_crows_nest',
    descKey: 'room_crows_nest_desc',
    baseAffinities: [
      { fearType: 'physical', bonus: 0.35 },
      { fearType: 'visual', bonus: 0.2 },
    ],
    baseStats: { fear: 20, thrill: 50, atmosphere: 25, fun: 20 },
    unlockCost: 14000,
    unlockCondition: { minDay: 8, requiredRoom: 'brig' },
    imagePath: '/images/room/crows-nest.webp',
    availableProps: ['strobe_light', 'surround_speaker', 'vibration_floor', 'temp_controller'],
    themeTags: ['pirate_ship'],
  },

  // ═══════════════════════════════════════════════
  // ABANDONED MINE (6)
  // ═══════════════════════════════════════════════
  mine_shaft: {
    id: 'mine_shaft',
    nameKey: 'room_mine_shaft',
    descKey: 'room_mine_shaft_desc',
    baseAffinities: [
      { fearType: 'physical', bonus: 0.3 },
      { fearType: 'auditory', bonus: 0.2 },
    ],
    baseStats: { fear: 30, thrill: 30, atmosphere: 30, fun: 10 },
    unlockCost: 0,
    imagePath: '/images/room/mine-shaft.webp',
    availableProps: ['cobwebs', 'chains', 'vibration_floor', 'temp_controller', 'surround_speaker'],
    themeTags: ['abandoned_mine'],
  },
  crystal_cave: {
    id: 'crystal_cave',
    nameKey: 'room_crystal_cave',
    descKey: 'room_crystal_cave_desc',
    baseAffinities: [
      { fearType: 'visual', bonus: 0.35 },
      { fearType: 'auditory', bonus: 0.15 },
    ],
    baseStats: { fear: 15, thrill: 10, atmosphere: 60, fun: 25 },
    unlockCost: 7000,
    unlockCondition: { requiredRoom: 'mine_shaft' },
    imagePath: '/images/room/crystal-cave.webp',
    availableProps: ['strobe_light', 'hologram_device', 'scent_sprayer', 'surround_speaker'],
    themeTags: ['abandoned_mine'],
  },
  explosives_storage: {
    id: 'explosives_storage',
    nameKey: 'room_explosives_storage',
    descKey: 'room_explosives_storage_desc',
    baseAffinities: [
      { fearType: 'physical', bonus: 0.4 },
      { fearType: 'auditory', bonus: 0.25 },
    ],
    baseStats: { fear: 40, thrill: 50, atmosphere: 10, fun: 10 },
    unlockCost: 16000,
    unlockCondition: { minDay: 10, requiredRoom: 'mine_shaft' },
    imagePath: '/images/room/explosives-storage.webp',
    availableProps: ['vibration_floor', 'surround_speaker', 'strobe_light', 'auto_lock'],
    themeTags: ['abandoned_mine'],
  },
  underground_lake: {
    id: 'underground_lake',
    nameKey: 'room_underground_lake',
    descKey: 'room_underground_lake_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.3 },
      { fearType: 'auditory', bonus: 0.25 },
    ],
    baseStats: { fear: 30, thrill: 10, atmosphere: 55, fun: 15 },
    unlockCost: 20000,
    unlockCondition: { minDay: 12, requiredRoom: 'crystal_cave' },
    imagePath: '/images/room/underground-lake.webp',
    availableProps: ['dry_ice', 'scent_sprayer', 'surround_speaker', 'temp_controller'],
    themeTags: ['abandoned_mine'],
  },
  rail_tunnel: {
    id: 'rail_tunnel',
    nameKey: 'room_rail_tunnel',
    descKey: 'room_rail_tunnel_desc',
    baseAffinities: [
      { fearType: 'physical', bonus: 0.3 },
      { fearType: 'psychological', bonus: 0.2 },
    ],
    baseStats: { fear: 35, thrill: 35, atmosphere: 25, fun: 10 },
    unlockCost: 12000,
    unlockCondition: { minDay: 6, requiredRoom: 'mine_shaft' },
    imagePath: '/images/room/rail-tunnel.webp',
    availableProps: ['vibration_floor', 'chains', 'strobe_light', 'surround_speaker', 'auto_lock'],
    themeTags: ['abandoned_mine'],
  },
  elevator_shaft: {
    id: 'elevator_shaft',
    nameKey: 'room_elevator_shaft',
    descKey: 'room_elevator_shaft_desc',
    baseAffinities: [
      { fearType: 'physical', bonus: 0.35 },
      { fearType: 'psychological', bonus: 0.2 },
    ],
    baseStats: { fear: 40, thrill: 40, atmosphere: 20, fun: 10 },
    unlockCost: 10000,
    unlockCondition: { minDay: 6, requiredRoom: 'mine_shaft' },
    imagePath: '/images/room/elevator-shaft.webp',
    availableProps: ['chains', 'vibration_floor', 'surround_speaker', 'auto_lock', 'strobe_light'],
    themeTags: ['abandoned_mine'],
  },
  ore_processing: {
    id: 'ore_processing',
    nameKey: 'room_ore_processing',
    descKey: 'room_ore_processing_desc',
    baseAffinities: [
      { fearType: 'auditory', bonus: 0.3 },
      { fearType: 'physical', bonus: 0.2 },
    ],
    baseStats: { fear: 25, thrill: 30, atmosphere: 25, fun: 20 },
    unlockCost: 14000,
    unlockCondition: { minDay: 8, requiredRoom: 'mine_shaft' },
    imagePath: '/images/room/ore-processing.webp',
    availableProps: ['vibration_floor', 'surround_speaker', 'temp_controller', 'strobe_light'],
    themeTags: ['abandoned_mine'],
  },
  collapsed_tunnel: {
    id: 'collapsed_tunnel',
    nameKey: 'room_collapsed_tunnel',
    descKey: 'room_collapsed_tunnel_desc',
    baseAffinities: [
      { fearType: 'physical', bonus: 0.4 },
      { fearType: 'psychological', bonus: 0.25 },
    ],
    baseStats: { fear: 55, thrill: 35, atmosphere: 20, fun: 5 },
    unlockCost: 22000,
    unlockCondition: { minDay: 12, minFame: 100, requiredRoom: 'rail_tunnel' },
    imagePath: '/images/room/collapsed-tunnel.webp',
    availableProps: ['vibration_floor', 'surround_speaker', 'dry_ice', 'auto_lock', 'chains'],
    themeTags: ['abandoned_mine'],
  },
  miners_quarters: {
    id: 'miners_quarters',
    nameKey: 'room_miners_quarters',
    descKey: 'room_miners_quarters_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.3 },
      { fearType: 'visual', bonus: 0.2 },
    ],
    baseStats: { fear: 30, thrill: 10, atmosphere: 45, fun: 15 },
    unlockCost: 0,
    imagePath: '/images/room/miners-quarters.webp',
    availableProps: ['cobwebs', 'candles', 'eerie_painting', 'scent_sprayer', 'creaky_floor'],
    themeTags: ['abandoned_mine'],
  },

  // ═══════════════════════════════════════════════
  // GOTHIC CASTLE (6)
  // ═══════════════════════════════════════════════
  throne_room: {
    id: 'throne_room',
    nameKey: 'room_throne_room',
    descKey: 'room_throne_room_desc',
    baseAffinities: [
      { fearType: 'visual', bonus: 0.25 },
      { fearType: 'psychological', bonus: 0.3 },
    ],
    baseStats: { fear: 40, thrill: 10, atmosphere: 50, fun: 10 },
    unlockCost: 5000,
    imagePath: '/images/room/throne-room.webp',
    availableProps: ['candles', 'eerie_painting', 'skull_decor', 'cobwebs', 'scent_sprayer'],
    themeTags: ['gothic_castle'],
  },
  wine_cellar: {
    id: 'wine_cellar',
    nameKey: 'room_wine_cellar',
    descKey: 'room_wine_cellar_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.3 },
      { fearType: 'visual', bonus: 0.15 },
    ],
    baseStats: { fear: 30, thrill: 15, atmosphere: 45, fun: 20 },
    unlockCost: 8000,
    unlockCondition: { requiredRoom: 'throne_room' },
    imagePath: '/images/room/wine-cellar.webp',
    availableProps: ['cobwebs', 'candles', 'blood_pool', 'dry_ice', 'temp_controller'],
    themeTags: ['gothic_castle'],
  },
  tower_chamber: {
    id: 'tower_chamber',
    nameKey: 'room_tower_chamber',
    descKey: 'room_tower_chamber_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.35 },
      { fearType: 'physical', bonus: 0.2 },
    ],
    baseStats: { fear: 50, thrill: 25, atmosphere: 35, fun: 5 },
    unlockCost: 25000,
    unlockCondition: { minDay: 14, minFame: 150, requiredRoom: 'throne_room' },
    imagePath: '/images/room/tower-chamber.webp',
    availableProps: ['chains', 'auto_lock', 'strobe_light', 'surround_speaker', 'hologram_device'],
    themeTags: ['gothic_castle'],
  },

  // ═══════════════════════════════════════════════
  // UNDERGROUND BUNKER (6)
  // ═══════════════════════════════════════════════
  server_room: {
    id: 'server_room',
    nameKey: 'room_server_room',
    descKey: 'room_server_room_desc',
    baseAffinities: [
      { fearType: 'auditory', bonus: 0.3 },
      { fearType: 'visual', bonus: 0.2 },
    ],
    baseStats: { fear: 20, thrill: 25, atmosphere: 35, fun: 25 },
    unlockCost: 0,
    imagePath: '/images/room/server-room.webp',
    availableProps: ['strobe_light', 'surround_speaker', 'hologram_device', 'temp_controller'],
    themeTags: ['underground_bunker'],
  },
  quarantine_zone: {
    id: 'quarantine_zone',
    nameKey: 'room_quarantine_zone',
    descKey: 'room_quarantine_zone_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.35 },
      { fearType: 'physical', bonus: 0.2 },
    ],
    baseStats: { fear: 45, thrill: 30, atmosphere: 25, fun: 5 },
    unlockCost: 18000,
    unlockCondition: { minDay: 10, requiredRoom: 'server_room' },
    imagePath: '/images/room/quarantine-zone.webp',
    availableProps: ['auto_lock', 'surround_speaker', 'vibration_floor', 'scent_sprayer', 'strobe_light'],
    themeTags: ['underground_bunker'],
  },
  armory: {
    id: 'armory',
    nameKey: 'room_armory',
    descKey: 'room_armory_desc',
    baseAffinities: [
      { fearType: 'physical', bonus: 0.35 },
      { fearType: 'visual', bonus: 0.2 },
    ],
    baseStats: { fear: 35, thrill: 40, atmosphere: 15, fun: 15 },
    unlockCost: 14000,
    unlockCondition: { minDay: 7, requiredRoom: 'server_room' },
    imagePath: '/images/room/armory.webp',
    availableProps: ['chains', 'skull_decor', 'blood_pool', 'vibration_floor', 'auto_lock'],
    themeTags: ['underground_bunker'],
  },
  command_center: {
    id: 'command_center',
    nameKey: 'room_command_center',
    descKey: 'room_command_center_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.3 },
      { fearType: 'auditory', bonus: 0.25 },
    ],
    baseStats: { fear: 35, thrill: 20, atmosphere: 40, fun: 15 },
    unlockCost: 0,
    imagePath: '/images/room/command-center.webp',
    availableProps: ['strobe_light', 'surround_speaker', 'hologram_device', 'temp_controller'],
    themeTags: ['underground_bunker'],
  },
  ventilation_shaft: {
    id: 'ventilation_shaft',
    nameKey: 'room_ventilation_shaft',
    descKey: 'room_ventilation_shaft_desc',
    baseAffinities: [
      { fearType: 'physical', bonus: 0.35 },
      { fearType: 'auditory', bonus: 0.25 },
    ],
    baseStats: { fear: 40, thrill: 35, atmosphere: 20, fun: 10 },
    unlockCost: 10000,
    unlockCondition: { minDay: 6, requiredRoom: 'command_center' },
    imagePath: '/images/room/ventilation-shaft.webp',
    availableProps: ['vibration_floor', 'surround_speaker', 'auto_lock', 'scent_sprayer', 'dry_ice'],
    themeTags: ['underground_bunker'],
  },
  decontamination: {
    id: 'decontamination',
    nameKey: 'room_decontamination',
    descKey: 'room_decontamination_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.35 },
      { fearType: 'visual', bonus: 0.25 },
    ],
    baseStats: { fear: 50, thrill: 25, atmosphere: 30, fun: 5 },
    unlockCost: 25000,
    unlockCondition: { minDay: 14, minFame: 150, requiredRoom: 'quarantine_zone' },
    imagePath: '/images/room/decontamination.webp',
    availableProps: ['strobe_light', 'auto_lock', 'temp_controller', 'scent_sprayer', 'hologram_device'],
    themeTags: ['underground_bunker'],
  },

  // ═══════════════════════════════════════════════
  // CURSED FOREST (6)
  // ═══════════════════════════════════════════════
  witch_hut: {
    id: 'witch_hut',
    nameKey: 'room_witch_hut',
    descKey: 'room_witch_hut_desc',
    baseAffinities: [
      { fearType: 'visual', bonus: 0.3 },
      { fearType: 'psychological', bonus: 0.25 },
    ],
    baseStats: { fear: 40, thrill: 10, atmosphere: 40, fun: 15 },
    unlockCost: 0,
    imagePath: '/images/room/witch-hut.webp',
    availableProps: ['candles', 'cobwebs', 'blood_pool', 'scent_sprayer', 'eerie_painting'],
    themeTags: ['cursed_forest'],
  },
  treehouse: {
    id: 'treehouse',
    nameKey: 'room_treehouse',
    descKey: 'room_treehouse_desc',
    baseAffinities: [
      { fearType: 'physical', bonus: 0.25 },
      { fearType: 'auditory', bonus: 0.2 },
    ],
    baseStats: { fear: 15, thrill: 30, atmosphere: 30, fun: 30 },
    unlockCost: 5000,
    unlockCondition: { requiredRoom: 'witch_hut' },
    imagePath: '/images/room/treehouse.webp',
    availableProps: ['cobwebs', 'vibration_floor', 'surround_speaker', 'dry_ice'],
    themeTags: ['cursed_forest'],
  },
  swamp_path: {
    id: 'swamp_path',
    nameKey: 'room_swamp_path',
    descKey: 'room_swamp_path_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.3 },
      { fearType: 'auditory', bonus: 0.2 },
    ],
    baseStats: { fear: 35, thrill: 10, atmosphere: 50, fun: 10 },
    unlockCost: 9000,
    unlockCondition: { requiredRoom: 'witch_hut' },
    imagePath: '/images/room/swamp-path.webp',
    availableProps: ['scent_sprayer', 'dry_ice', 'surround_speaker', 'temp_controller'],
    themeTags: ['cursed_forest'],
  },
  fairy_ring: {
    id: 'fairy_ring',
    nameKey: 'room_fairy_ring',
    descKey: 'room_fairy_ring_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.4 },
      { fearType: 'visual', bonus: 0.2 },
    ],
    baseStats: { fear: 50, thrill: 15, atmosphere: 40, fun: 10 },
    unlockCost: 16000,
    unlockCondition: { minDay: 10, requiredRoom: 'swamp_path' },
    imagePath: '/images/room/fairy-ring.webp',
    availableProps: ['hologram_device', 'strobe_light', 'scent_sprayer', 'surround_speaker'],
    themeTags: ['cursed_forest'],
  },
  ancient_ruin: {
    id: 'ancient_ruin',
    nameKey: 'room_ancient_ruin',
    descKey: 'room_ancient_ruin_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.35 },
      { fearType: 'visual', bonus: 0.25 },
    ],
    baseStats: { fear: 45, thrill: 10, atmosphere: 55, fun: 5 },
    unlockCost: 22000,
    unlockCondition: { minDay: 14, minFame: 120, requiredRoom: 'fairy_ring' },
    imagePath: '/images/room/ancient-ruin.webp',
    availableProps: ['cobwebs', 'eerie_painting', 'dry_ice', 'surround_speaker', 'skull_decor'],
    themeTags: ['cursed_forest'],
  },
  hollow_tree: {
    id: 'hollow_tree',
    nameKey: 'room_hollow_tree',
    descKey: 'room_hollow_tree_desc',
    baseAffinities: [
      { fearType: 'visual', bonus: 0.3 },
      { fearType: 'psychological', bonus: 0.25 },
    ],
    baseStats: { fear: 35, thrill: 10, atmosphere: 50, fun: 15 },
    unlockCost: 6000,
    unlockCondition: { requiredRoom: 'witch_hut' },
    imagePath: '/images/room/hollow-tree.webp',
    availableProps: ['cobwebs', 'hologram_device', 'scent_sprayer', 'dry_ice', 'surround_speaker'],
    themeTags: ['cursed_forest'],
  },
  spider_nest: {
    id: 'spider_nest',
    nameKey: 'room_spider_nest',
    descKey: 'room_spider_nest_desc',
    baseAffinities: [
      { fearType: 'visual', bonus: 0.35 },
      { fearType: 'physical', bonus: 0.25 },
    ],
    baseStats: { fear: 55, thrill: 25, atmosphere: 25, fun: 5 },
    unlockCost: 12000,
    unlockCondition: { minDay: 7, requiredRoom: 'hollow_tree' },
    imagePath: '/images/room/spider-nest.webp',
    availableProps: ['cobwebs', 'strobe_light', 'vibration_floor', 'scent_sprayer', 'auto_lock'],
    themeTags: ['cursed_forest'],
  },
  moonlit_clearing: {
    id: 'moonlit_clearing',
    nameKey: 'room_moonlit_clearing',
    descKey: 'room_moonlit_clearing_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.4 },
      { fearType: 'auditory', bonus: 0.2 },
    ],
    baseStats: { fear: 40, thrill: 10, atmosphere: 55, fun: 10 },
    unlockCost: 18000,
    unlockCondition: { minDay: 10, minFame: 80, requiredRoom: 'swamp_path' },
    imagePath: '/images/room/moonlit-clearing.webp',
    availableProps: ['hologram_device', 'surround_speaker', 'dry_ice', 'scent_sprayer', 'candles'],
    themeTags: ['cursed_forest'],
  },
  abandoned_cabin: {
    id: 'abandoned_cabin',
    nameKey: 'room_abandoned_cabin',
    descKey: 'room_abandoned_cabin_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.3 },
      { fearType: 'visual', bonus: 0.2 },
    ],
    baseStats: { fear: 30, thrill: 15, atmosphere: 40, fun: 20 },
    unlockCost: 0,
    imagePath: '/images/room/abandoned-cabin.webp',
    availableProps: ['cobwebs', 'candles', 'creaky_floor', 'eerie_painting', 'scent_sprayer'],
    themeTags: ['cursed_forest'],
  },

  // ═══════════════════════════════════════════════
  // SHARED / CROSS-THEME ROOMS
  // ═══════════════════════════════════════════════
  attic: {
    id: 'attic',
    nameKey: 'room_attic',
    descKey: 'room_attic_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.3 },
      { fearType: 'auditory', bonus: 0.15 },
    ],
    baseStats: { fear: 35, thrill: 10, atmosphere: 45, fun: 15 },
    unlockCost: 0,
    imagePath: '/images/room/attic.webp',
    availableProps: ['cobwebs', 'candles', 'eerie_painting', 'creaky_floor', 'scent_sprayer'],
    themeTags: ['school_back_mountain'],
  },
  secret_passage: {
    id: 'secret_passage',
    nameKey: 'room_secret_passage',
    descKey: 'room_secret_passage_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.35 },
      { fearType: 'physical', bonus: 0.15 },
    ],
    baseStats: { fear: 40, thrill: 20, atmosphere: 35, fun: 15 },
    unlockCost: 10000,
    unlockCondition: { minDay: 5, requiredRoom: 'dark_corridor' },
    imagePath: '/images/room/secret-passage.webp',
    availableProps: ['cobwebs', 'chains', 'dry_ice', 'auto_lock', 'scent_sprayer'],
    themeTags: ['haunted_house'],
  },
  morgue: {
    id: 'morgue',
    nameKey: 'room_morgue',
    descKey: 'room_morgue_desc',
    baseAffinities: [
      { fearType: 'visual', bonus: 0.3 },
      { fearType: 'psychological', bonus: 0.3 },
    ],
    baseStats: { fear: 55, thrill: 20, atmosphere: 30, fun: 5 },
    unlockCost: 15000,
    unlockCondition: { minDay: 8, minFame: 80 },
    imagePath: '/images/room/morgue.webp',
    availableProps: ['blood_pool', 'temp_controller', 'dry_ice', 'surround_speaker', 'auto_lock'],
    themeTags: ['abandoned_hospital'],
  },
  surgery_room: {
    id: 'surgery_room',
    nameKey: 'room_surgery_room',
    descKey: 'room_surgery_room_desc',
    baseAffinities: [
      { fearType: 'physical', bonus: 0.35 },
      { fearType: 'visual', bonus: 0.25 },
    ],
    baseStats: { fear: 50, thrill: 35, atmosphere: 20, fun: 5 },
    unlockCost: 18000,
    unlockCondition: { minDay: 10, requiredRoom: 'lab' },
    imagePath: '/images/room/surgery-room.webp',
    availableProps: ['blood_pool', 'strobe_light', 'vibration_floor', 'surround_speaker', 'temp_controller'],
    themeTags: ['abandoned_hospital'],
  },
  music_room: {
    id: 'music_room',
    nameKey: 'room_music_room',
    descKey: 'room_music_room_desc',
    baseAffinities: [
      { fearType: 'auditory', bonus: 0.4 },
      { fearType: 'psychological', bonus: 0.2 },
    ],
    baseStats: { fear: 25, thrill: 15, atmosphere: 50, fun: 20 },
    unlockCost: 6000,
    unlockCondition: { requiredRoom: 'attic' },
    imagePath: '/images/room/music-room.webp',
    availableProps: ['surround_speaker', 'candles', 'eerie_painting', 'scent_sprayer'],
    themeTags: ['school_back_mountain'],
  },
  science_lab: {
    id: 'science_lab',
    nameKey: 'room_science_lab',
    descKey: 'room_science_lab_desc',
    baseAffinities: [
      { fearType: 'visual', bonus: 0.3 },
      { fearType: 'physical', bonus: 0.25 },
    ],
    baseStats: { fear: 30, thrill: 35, atmosphere: 15, fun: 25 },
    unlockCost: 12000,
    unlockCondition: { minDay: 6, requiredRoom: 'music_room' },
    imagePath: '/images/room/science-lab.webp',
    availableProps: ['strobe_light', 'hologram_device', 'temp_controller', 'surround_speaker', 'vibration_floor'],
    themeTags: ['school_back_mountain'],
  },

  // ═══════════════════════════════════════════════
  // NEW: ABANDONED HOSPITAL (+3)
  // ═══════════════════════════════════════════════
  ward_room: {
    id: 'ward_room',
    nameKey: 'room_ward_room',
    descKey: 'room_ward_room_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.3 },
      { fearType: 'visual', bonus: 0.2 },
    ],
    baseStats: { fear: 35, thrill: 15, atmosphere: 40, fun: 10 },
    unlockCost: 3000,
    imagePath: '/images/room/ward-room.webp',
    availableProps: ['cobwebs', 'blood_pool', 'surround_speaker', 'scent_sprayer', 'auto_lock'],
    themeTags: ['abandoned_hospital'],
  },
  reception_hall: {
    id: 'reception_hall',
    nameKey: 'room_reception_hall',
    descKey: 'room_reception_hall_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.25 },
      { fearType: 'auditory', bonus: 0.2 },
    ],
    baseStats: { fear: 20, thrill: 10, atmosphere: 45, fun: 25 },
    unlockCost: 0,
    imagePath: '/images/room/reception-hall.webp',
    availableProps: ['cobwebs', 'eerie_painting', 'surround_speaker', 'strobe_light'],
    themeTags: ['abandoned_hospital'],
  },
  x_ray_room: {
    id: 'x_ray_room',
    nameKey: 'room_x_ray_room',
    descKey: 'room_x_ray_room_desc',
    baseAffinities: [
      { fearType: 'visual', bonus: 0.35 },
      { fearType: 'physical', bonus: 0.2 },
    ],
    baseStats: { fear: 45, thrill: 30, atmosphere: 25, fun: 10 },
    unlockCost: 12000,
    unlockCondition: { minDay: 7, requiredRoom: 'lab' },
    imagePath: '/images/room/x-ray-room.webp',
    availableProps: ['strobe_light', 'hologram_device', 'temp_controller', 'surround_speaker', 'auto_lock'],
    themeTags: ['abandoned_hospital'],
  },

  // ═══════════════════════════════════════════════
  // NEW: SCHOOL BACK MOUNTAIN (+4)
  // ═══════════════════════════════════════════════
  classroom: {
    id: 'classroom',
    nameKey: 'room_classroom',
    descKey: 'room_classroom_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.3 },
      { fearType: 'auditory', bonus: 0.15 },
    ],
    baseStats: { fear: 25, thrill: 10, atmosphere: 45, fun: 20 },
    unlockCost: 0,
    imagePath: '/images/room/classroom.webp',
    availableProps: ['cobwebs', 'eerie_painting', 'surround_speaker', 'candles', 'scent_sprayer'],
    themeTags: ['school_back_mountain'],
  },
  art_room: {
    id: 'art_room',
    nameKey: 'room_art_room',
    descKey: 'room_art_room_desc',
    baseAffinities: [
      { fearType: 'visual', bonus: 0.35 },
      { fearType: 'psychological', bonus: 0.2 },
    ],
    baseStats: { fear: 35, thrill: 15, atmosphere: 40, fun: 15 },
    unlockCost: 7000,
    unlockCondition: { requiredRoom: 'classroom' },
    imagePath: '/images/room/art-room.webp',
    availableProps: ['eerie_painting', 'strobe_light', 'hologram_device', 'scent_sprayer'],
    themeTags: ['school_back_mountain'],
  },
  gymnasium: {
    id: 'gymnasium',
    nameKey: 'room_gymnasium',
    descKey: 'room_gymnasium_desc',
    baseAffinities: [
      { fearType: 'physical', bonus: 0.3 },
      { fearType: 'auditory', bonus: 0.25 },
    ],
    baseStats: { fear: 20, thrill: 35, atmosphere: 20, fun: 30 },
    unlockCost: 10000,
    unlockCondition: { minDay: 5, requiredRoom: 'classroom' },
    imagePath: '/images/room/gymnasium.webp',
    availableProps: ['surround_speaker', 'vibration_floor', 'strobe_light', 'auto_lock'],
    themeTags: ['school_back_mountain'],
  },
  rooftop: {
    id: 'rooftop',
    nameKey: 'room_rooftop',
    descKey: 'room_rooftop_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.4 },
      { fearType: 'physical', bonus: 0.2 },
    ],
    baseStats: { fear: 45, thrill: 25, atmosphere: 35, fun: 5 },
    unlockCost: 18000,
    unlockCondition: { minDay: 10, minFame: 80, requiredRoom: 'gymnasium' },
    imagePath: '/images/room/rooftop.webp',
    availableProps: ['dry_ice', 'surround_speaker', 'vibration_floor', 'scent_sprayer', 'hologram_device'],
    themeTags: ['school_back_mountain'],
  },

  // ═══════════════════════════════════════════════
  // NEW: ANCIENT TEMPLE (+1)
  // ═══════════════════════════════════════════════
  crypt: {
    id: 'crypt',
    nameKey: 'room_crypt',
    descKey: 'room_crypt_desc',
    baseAffinities: [
      { fearType: 'visual', bonus: 0.35 },
      { fearType: 'psychological', bonus: 0.25 },
    ],
    baseStats: { fear: 55, thrill: 15, atmosphere: 40, fun: 5 },
    unlockCost: 20000,
    unlockCondition: { minDay: 10, requiredRoom: 'mummy_tomb' },
    imagePath: '/images/room/crypt.webp',
    availableProps: ['cobwebs', 'skull_decor', 'dry_ice', 'candles', 'auto_lock'],
    themeTags: ['ancient_temple'],
  },

  // ═══════════════════════════════════════════════
  // NEW: PIRATE SHIP (+2)
  // ═══════════════════════════════════════════════
  powder_room: {
    id: 'powder_room',
    nameKey: 'room_powder_room',
    descKey: 'room_powder_room_desc',
    baseAffinities: [
      { fearType: 'physical', bonus: 0.4 },
      { fearType: 'auditory', bonus: 0.2 },
    ],
    baseStats: { fear: 40, thrill: 45, atmosphere: 15, fun: 10 },
    unlockCost: 15000,
    unlockCondition: { minDay: 8, requiredRoom: 'cargo_hold' },
    imagePath: '/images/room/powder-room.webp',
    availableProps: ['vibration_floor', 'surround_speaker', 'dry_ice', 'auto_lock', 'chains'],
    themeTags: ['pirate_ship'],
  },
  navigation_room: {
    id: 'navigation_room',
    nameKey: 'room_navigation_room',
    descKey: 'room_navigation_room_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.3 },
      { fearType: 'visual', bonus: 0.2 },
    ],
    baseStats: { fear: 30, thrill: 15, atmosphere: 45, fun: 15 },
    unlockCost: 8000,
    unlockCondition: { requiredRoom: 'captain_cabin' },
    imagePath: '/images/room/navigation-room.webp',
    availableProps: ['candles', 'eerie_painting', 'hologram_device', 'surround_speaker'],
    themeTags: ['pirate_ship'],
  },

  // ═══════════════════════════════════════════════
  // NEW: GOTHIC CASTLE (+3)
  // ═══════════════════════════════════════════════
  banquet_hall: {
    id: 'banquet_hall',
    nameKey: 'room_banquet_hall',
    descKey: 'room_banquet_hall_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.3 },
      { fearType: 'visual', bonus: 0.2 },
    ],
    baseStats: { fear: 30, thrill: 10, atmosphere: 50, fun: 15 },
    unlockCost: 10000,
    unlockCondition: { requiredRoom: 'throne_room' },
    imagePath: '/images/room/banquet-hall.webp',
    availableProps: ['candles', 'eerie_painting', 'cobwebs', 'scent_sprayer', 'surround_speaker'],
    themeTags: ['gothic_castle'],
  },
  chapel: {
    id: 'chapel',
    nameKey: 'room_chapel',
    descKey: 'room_chapel_desc',
    baseAffinities: [
      { fearType: 'psychological', bonus: 0.4 },
      { fearType: 'auditory', bonus: 0.2 },
    ],
    baseStats: { fear: 45, thrill: 10, atmosphere: 55, fun: 5 },
    unlockCost: 16000,
    unlockCondition: { minDay: 8, requiredRoom: 'throne_room' },
    imagePath: '/images/room/chapel.webp',
    availableProps: ['candles', 'hologram_device', 'surround_speaker', 'scent_sprayer', 'dry_ice'],
    themeTags: ['gothic_castle'],
  },
  torture_chamber: {
    id: 'torture_chamber',
    nameKey: 'room_torture_chamber',
    descKey: 'room_torture_chamber_desc',
    baseAffinities: [
      { fearType: 'physical', bonus: 0.4 },
      { fearType: 'visual', bonus: 0.3 },
    ],
    baseStats: { fear: 65, thrill: 30, atmosphere: 20, fun: 0 },
    unlockCost: 28000,
    unlockCondition: { minDay: 14, minFame: 150, requiredRoom: 'dungeon' },
    imagePath: '/images/room/torture-chamber.webp',
    availableProps: ['chains', 'blood_pool', 'skull_decor', 'vibration_floor', 'auto_lock'],
    themeTags: ['gothic_castle'],
  },

  // ═══════════════════════════════════════════════
  // NEW: UNDERGROUND BUNKER (+2)
  // ═══════════════════════════════════════════════
  armory_hall: {
    id: 'armory_hall',
    nameKey: 'room_armory_hall',
    descKey: 'room_armory_hall_desc',
    baseAffinities: [
      { fearType: 'physical', bonus: 0.3 },
      { fearType: 'visual', bonus: 0.25 },
    ],
    baseStats: { fear: 35, thrill: 35, atmosphere: 20, fun: 15 },
    unlockCost: 12000,
    unlockCondition: { minDay: 6, requiredRoom: 'armory' },
    imagePath: '/images/room/armory-hall.webp',
    availableProps: ['chains', 'skull_decor', 'strobe_light', 'vibration_floor', 'auto_lock'],
    themeTags: ['underground_bunker'],
  },
  science_room: {
    id: 'science_room',
    nameKey: 'room_science_room',
    descKey: 'room_science_room_desc',
    baseAffinities: [
      { fearType: 'visual', bonus: 0.3 },
      { fearType: 'psychological', bonus: 0.2 },
    ],
    baseStats: { fear: 30, thrill: 25, atmosphere: 30, fun: 20 },
    unlockCost: 9000,
    unlockCondition: { requiredRoom: 'server_room' },
    imagePath: '/images/room/science-room.webp',
    availableProps: ['strobe_light', 'hologram_device', 'temp_controller', 'surround_speaker'],
    themeTags: ['underground_bunker'],
  },
};

export const ALL_ROOM_TYPE_IDS = Object.keys(ROOM_TYPES) as RoomTypeId[];
